import logError from '@alltrails/core/utils/logError';
import type { CustomRouteMap } from '@alltrails/types/CustomRoute';
import { CustomRouteSegment } from '../../types/CustomRoute';
import { ElevationPoint, MapWithRoutes } from '../../types/Geo';
import getMapElevationData from '../getMapElevationData';
import { bySequenceNum, getLines, getSegmentPolylinesForMap } from './getSegmentPolylinesForMap';

const transformMapForElevationData = (map: CustomRouteMap): Partial<MapWithRoutes> => {
  return {
    routes: getLines(map).map(route => ({
      lineSegments: route.lineSegments.map(segment => ({
        sequence_num: segment.sequence_num,
        polyline: segment.polyline,
        freeDraw: segment.freeDraw
      }))
    }))
  };
};

const getSegmentsForMap = (map: CustomRouteMap): CustomRouteSegment[] => {
  const elevationData = getMapElevationData(transformMapForElevationData(map));
  // getSegmentPolylinesForMap already returns segments sorted by sequence_num with color + decoded
  // paths resolved. We sort the routes with the same comparator so we can zip route metadata and
  // elevation data onto each polyline segment by index.
  const polylineSegments = getSegmentPolylinesForMap(map);
  const sortedRoutes = [...getLines(map)].sort(bySequenceNum);

  if (elevationData.length !== sortedRoutes.length) {
    logError('Elevation data does not match the number of routes');
  }

  return polylineSegments.map((polylineSegment, index) => {
    const { id: serverId, lineGeoStats, estimatedTimeToComplete, name, description, isLinkedToNextSegment } = sortedRoutes[index];
    const { distanceTotal: distance, elevationGain, elevationLoss } = lineGeoStats;
    const distanceOffset = elevationData[index]?.[0]?.[0] ?? 0;
    const elevationPoints = (elevationData[index] || []).map(
      // Make distanceTo relative to the segment rather than to the start of the route
      // This makes our state easier to manage when adding and removing segments, paths, and points
      ([distanceTo, ...rest]) => [distanceTo - distanceOffset, ...rest] as ElevationPoint
    );

    return {
      serverId,
      color: polylineSegment.color,
      description,
      elevationPoints,
      name,
      paths: polylineSegment.paths,
      distance,
      elevationGain,
      elevationLoss,
      estimatedTime: estimatedTimeToComplete,
      isLinkedToNextSegment
    };
  });
};

export default getSegmentsForMap;
